import { ref, watch, toValue, onMounted, onBeforeUnmount } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { masifiWhatsappQrStatusAPI } from 'dashboard/api/masifiWhatsappQr';
import {
  WHATSAPP_QR_STATUS_EVENT,
  isWhatsappQrInbox,
} from 'dashboard/helper/masifiWhatsappQr';

/**
 * Estado grueso del número de WhatsApp QR de un inbox, para los agentes (F2 ruling 15):
 * ok | reconnecting | paused | disabled | disconnected, o `null` (inbox no-QR, sin acceso o error de red —
 * nunca bloquear el composer por un fallo del banner). Pide el estado al montar y al cambiar de inbox, y se
 * actualiza en vivo con el evento de ActionCable filtrado por `inbox_id`.
 * @param {import('vue').MaybeRefOrGetter<Object>} inboxRef - inbox (necesita `id` y `provider`)
 * @returns {{ state: import('vue').Ref<string|null> }}
 */
export function useMasifiWhatsappQrStatus(inboxRef) {
  const state = ref(null);
  // Descarta respuestas de un inbox anterior si la conversación cambió mientras esperaba.
  let requestedInboxId = null;
  // Eventos en vivo aplicados: si llega uno mientras el GET está en vuelo, el GET (leído antes) ya es viejo.
  let liveEvents = 0;

  const fetchStatus = async inboxId => {
    state.value = null;
    requestedInboxId = inboxId;
    if (inboxId === null) return;

    const liveAtStart = liveEvents;
    const isCurrent = () =>
      requestedInboxId === inboxId && liveEvents === liveAtStart;
    try {
      const { data } = await masifiWhatsappQrStatusAPI.get(inboxId);
      if (isCurrent()) state.value = data?.state ?? null;
    } catch {
      if (isCurrent()) state.value = null;
    }
  };

  const onStatus = data => {
    if (requestedInboxId !== null && data?.inbox_id === requestedInboxId) {
      liveEvents += 1;
      state.value = data.state ?? null;
    }
  };

  // Se observa solo el id (y si es QR): el objeto del inbox del store se recrea en cada refresco de la lista.
  const qrInboxId = () => {
    const inbox = toValue(inboxRef);
    return isWhatsappQrInbox(inbox) ? inbox.id : null;
  };
  watch(qrInboxId, fetchStatus, { immediate: true });
  onMounted(() => emitter.on(WHATSAPP_QR_STATUS_EVENT, onStatus));
  onBeforeUnmount(() => emitter.off(WHATSAPP_QR_STATUS_EVENT, onStatus));

  return { state };
}
